
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TestimonialsPanel from "@/components/premium/TestimonialsPanel";
import OffersPanel from "@/components/coaching/OffersPanel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Code, Layers, MessageSquare } from "lucide-react";

export default function Coaching() {
  const { user, session } = useAuth();
  const navigate = useNavigate();

  const handleScheduleInterview = () => {
    if (!user || !session) {
      toast.error("Please log in to book a coaching session");
      navigate("/login");
      return;
    }

    navigate("/mock-interview");
  };

  const interviewTypes = [
    {
      id: "ml_system_design",
      icon: Layers,
      title: "ML System Design",
      price: 225,
      badge: "Most Popular",
      description: "Master ML system design interviews with real-world scenarios",
      features: [
        "Design a Restaurant Recommendation System",
        "Design a Content Moderation Pipeline",
        "Design a Real-time Fraud Detection System"
      ]
    },
    {
      id: "ml_coding",
      icon: Code,
      title: "ML Modeling/Coding",
      price: 225,
      description: "Practice ML modeling and coding challenges",
      features: [
        "Implement a 2-layer MLP with ReLU",
        "Build a Logistic Regression from scratch",
        "Code Attention mechanism in PyTorch"
      ]
    },
    {
      id: "coding",
      icon: MessageSquare,
      title: "Mock Coding Interview",
      price: 199,
      description: "Practice general coding interviews with experienced engineers",
      features: [
        "Graph traversal and shortest paths",
        "Dynamic programming patterns",
        "System design data structures"
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-16">
        <div className="container max-w-6xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">1:1 ML Interview Coaching</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Get personalized coaching from Staff+ ML Engineers at FAANG companies. 
              Practice real interview questions and receive expert feedback to land your dream job.
            </p>
          </div>

          {/* Interview Types Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {interviewTypes.map((type) => {
              const Icon = type.icon;
              return (
                <Card 
                  key={type.id}
                  className="border-2 hover:border-primary/50 transition-colors"
                >
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      {type.badge && <Badge variant="secondary">{type.badge}</Badge>}
                      <div className="text-right">
                        <div className="text-2xl font-bold">${type.price}</div>
                        <div className="text-sm text-muted-foreground">60 minutes</div>
                      </div>
                    </div>
                    <CardTitle className="flex items-center gap-2">
                      <Icon className="h-5 w-5" />
                      {type.title}
                    </CardTitle>
                    <CardDescription>
                      {type.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2">
                      {type.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={handleScheduleInterview}
                    >
                      Schedule Interview
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left - Recent Offers */}
            <div>
              <OffersPanel />
            </div>
            
            {/* Right - Testimonials */}
            <div>
              <TestimonialsPanel />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
